import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchStoreActivities,
  fetchDistinctValuesForStoreActivities,
  type StoreActivityForManager,
} from "../../../../state/store-activities";
import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Badge,
  Dialog,
  Button,
  Flex,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { Link } from "react-router-dom";
import type { SearchParams } from "../../../Manager/types";
import { todayInPeriodTimezone } from "../../../../lib/dates";
import { supabase } from "../../../../lib/supabase";

type ProofSummary = {
  id: number;
  label: string;
  status: string;
};

async function fetchProofsForStoreActivity(
  saId: number,
  storeId: number,
  periodStartDate: string,
  periodEndDate: string,
): Promise<ProofSummary[]> {
  const results: ProofSummary[] = [];

  // Fetch store proof (scoped by period)
  const { data: storeProofData } = await supabase
    .from("proofs")
    .select("id, status, store_proof:store_proofs!inner(store_id)")
    .eq("target", "store")
    .eq("store_proof.store_id", storeId)
    .in("status", ["approved", "pending"])
    .gte("created_at", periodStartDate)
    .lte("created_at", periodEndDate + "T23:59:59.999Z")
    .order("created_at", { ascending: false })
    .limit(1);

  if (storeProofData && storeProofData.length > 0) {
    results.push({
      id: storeProofData[0].id,
      label: "Store",
      status: storeProofData[0].status,
    });
  }

  // Fetch product proofs for this store activity
  const { data: productProofData } = await supabase
    .from("proofs")
    .select(
      `id, status,
      product_proof:product_proofs!inner(
        store_activity_id,
        product_requirement:product_requirements(description, code, "order")
      )`,
    )
    .eq("target", "product")
    .eq("product_proof.store_activity_id", saId)
    .in("status", ["approved", "pending"])
    .order("created_at", { ascending: true });

  if (productProofData) {
    for (const p of productProofData) {
      const req = Array.isArray(p.product_proof)
        ? p.product_proof[0]
        : p.product_proof;
      const pr = req?.product_requirement;
      const desc = Array.isArray(pr) ? pr[0] : pr;
      results.push({
        id: p.id,
        label: desc?.description ?? desc?.code ?? "Product",
        status: p.status,
      });
    }
  }

  return results;
}

function FulfilledProofsDialog({
  sa,
  t,
}: {
  sa: StoreActivityForManager;
  t: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [proofs, setProofs] = useState<ProofSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchProofsForStoreActivity(
      sa.sa_id,
      sa.store_id,
      sa.period_start_date,
      sa.period_end_date,
    ).then((data) => {
      setProofs(data);
      setLoading(false);
    });
  }, [open, sa.sa_id, sa.store_id, sa.period_start_date, sa.period_end_date]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Badge color="yellow" style={{ cursor: "pointer" }}>
          {t("storeActivities.fulfilled")}
        </Badge>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>{t("storeActivities.viewProofs")}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {sa.store_name} — {sa.product_name}
        </Dialog.Description>
        {loading ? (
          <Flex justify="center" p="4">
            <Spinner size="3" />
          </Flex>
        ) : proofs.length === 0 ? (
          <Text color="gray" size="2">
            {t("storeActivities.noProofsFound")}
          </Text>
        ) : (
          <Flex direction="column" gap="2">
            {proofs.map((proof) => (
              <Button key={proof.id} variant="soft" asChild>
                <Link to={`/approbation/${proof.id}`}>{proof.label}</Link>
              </Button>
            ))}
          </Flex>
        )}
        <Flex mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {t("common.close")}
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function ProofStatusBadge({
  sa,
  t,
}: {
  sa: StoreActivityForManager;
  t: (key: string) => string;
}) {
  // Period-aware check
  if (sa.period_start_date && sa.period_end_date) {
    const today = todayInPeriodTimezone();
    if (today < sa.period_start_date) {
      return <Badge color="orange">{t("storeActivities.inFuture")}</Badge>;
    }
    if (today > sa.period_end_date) {
      return <Badge color="orange">{t("storeActivities.pastDue")}</Badge>;
    }
  }

  if (!sa.is_fulfilled) {
    return <Badge color="gray">{t("storeActivities.noProof")}</Badge>;
  }

  return <FulfilledProofsDialog sa={sa} t={t} />;
}

export function ManageStoreActivities() {
  const { t } = useTranslation();
  const fetchData = useCallback(
    (limit?: number, offset?: number, searchParams?: SearchParams) =>
      fetchStoreActivities(limit, offset, searchParams),
    [],
  );

  // Fetch distinct values for DynamicSearch filters (server-side RPC)
  const fetchDistinctValues = useCallback(
    async (
      filterKey: string,
      otherFilters?: Record<string, unknown>,
    ): Promise<{ value: string | number; label: string }[]> => {
      return fetchDistinctValuesForStoreActivities(filterKey, otherFilters);
    },
    [],
  );

  return (
    <Box p="4">
      <Manager.Root<StoreActivityForManager>
        title={t("storeActivities.title")}
        fetchData={fetchData}
        limit={50}
      >
        <Manager.Search>
          <Manager.DynamicSearch.Root fetchDistinctValues={fetchDistinctValues}>
            <Manager.DynamicSearch.Filter
              filterKey="period_ids"
              label={t("storeActivities.period")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="product_ids"
              label={t("storeActivities.product")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="supplier_ids"
              label={t("storeActivities.supplier")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="store_ids"
              label={t("storeActivities.store")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="user_ids"
              label={t("storeActivities.repAssignment")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="agency_ids"
              label={t("storeActivities.agency")}
            />
          </Manager.DynamicSearch.Root>
        </Manager.Search>

        <Manager.Table>
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.period")}
            accessor={(sa) => sa.period_name ?? "—"}
          />
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.product")}
            accessor={(sa) => sa.product_name ?? "—"}
          />
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.supplier")}
            accessor={(sa) => sa.supplier_name ?? "—"}
          />
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.store")}
            accessor={(sa) => sa.store_name ?? "—"}
          />
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.assignment")}
            accessor={(sa) => sa.rep_names || t("storeActivities.unassigned")}
          />
          <Manager.Column<StoreActivityForManager>
            header={t("storeActivities.proofStatus")}
            accessor={(sa) => <ProofStatusBadge sa={sa} t={t} />}
          />
        </Manager.Table>
      </Manager.Root>
    </Box>
  );
}
